import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

interface SwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
  /** Track color when on; defaults to primary. Pass a channel-* token to match a channel accent. */
  onColorClassName?: string;
  "aria-label"?: string;
}

/**
 * Small local toggle primitive — shadcn's Switch isn't present in
 * src/components/ui, and this feature is scoped to not touch shared ui/*.
 * Built as a native <button role="switch"> for full keyboard + SR support,
 * with a framer-motion spring on the thumb.
 */
export function Switch({
  checked,
  onCheckedChange,
  disabled = false,
  className,
  onColorClassName = "bg-primary",
  "aria-label": ariaLabel,
}: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200 ease-out",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        "disabled:cursor-not-allowed disabled:opacity-40",
        checked ? onColorClassName : "bg-slate-200",
        className
      )}
    >
      <motion.span
        className="inline-block rounded-full bg-white shadow-sm"
        style={{ height: "1.125rem", width: "1.125rem" }}
        animate={{ x: checked ? 22 : 4 }}
        transition={{ type: "spring", stiffness: 500, damping: 32 }}
      />
    </button>
  );
}
